/* eslint-disable @nx/enforce-module-boundaries */
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';

import { environment } from '@env';
import {
  AccountImage,
  NotificationEvent,
  NotificationEventEnum,
  NotificationQuery,
  NotificationQueryEnum,
  NotificationTemplate,
  NotificationType,
  NotificationTypeEnum,
} from '@models';
import { DateFns, StringFns, UtilFns } from '@utils';
import { DbSaveService } from '@data';
import { Entity, EntityError } from 'breeze-client';
import { MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';

import { TemplatePreviewDialogComponent, TemplatePreviewDialogParams } from './template-preview-dialog.component';

import { AccountDbQueryService } from '@account';
import * as _ from 'lodash';
import {
  BaseService,
  CoreAccountFinderDialog,
  CoreJobOrderFinderDialog,
  ImageInsertDialogComponent,
  ImageInsertDialogParams,
  TemplatesService,
  UtilDialogService,
} from '@core';
import { AccountAdminFinderDialog } from '../account-administrator/account-admin-finder.dialog';

import { AccountUserFinderDialog } from '../account-users/account-user-finder.dialog';
import { AccountImageHandlerComponent } from '../services/account-image-handler.component';

export interface NotificationTemplateDialogParams {
  accountId?: string;
  notificationTemplate: NotificationTemplate;
  isNew?: boolean;
  isEditable?: boolean;
  /** Pre-loaded templates for uniqueness checking (useful for global templates) */
  existingTemplates?: NotificationTemplate[];
  /** Provide the caller's DbSaveService to enable in-dialog saving */
  dbSaveService?: DbSaveService;
}

@Component({
  selector: 'prox-notification-template-dialog',
  templateUrl: './notification-template-dialog.component.html',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class NotificationTemplateDialogComponent implements OnInit {
  @ViewChild(AccountImageHandlerComponent) imageHandler!: AccountImageHandlerComponent;
  UtilFns = UtilFns;

  notificationTemplate!: NotificationTemplate;
  isNew = false;
  isEditable = false;
  isFreeForm = false;

  templates?: NotificationTemplate[];
  contextMenuItems: MenuItem[] = [];
  imageMenuItems: MenuItem[] = [];

  notificationEvents: NotificationEvent[] = [];
  notificationTypes: NotificationType[] = [];
  notificationQueries: NotificationQuery[] = [];
  namedNotificationQuery?: NotificationQuery;
  selectedEvent?: NotificationEvent;
  accountId?: string;
  oldImages: AccountImage[] = [];
  isKeyUnique = true;

  isPageReady = false;
  isBeingAdded!: boolean;
  emailAddress = '';
  phone = '';
  startDate = DateFns.fmtDate4(new Date());
  dbSaveService?: DbSaveService;
  isSaved = false;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig<NotificationTemplateDialogParams>,
    private dbQueryService: AccountDbQueryService,
    private baseService: BaseService,
    private httpClient: HttpClient,
    private templatesService: TemplatesService,
    private dialogService: UtilDialogService,
  ) {
    // because we pass this function to HtmlEditor
    this.selectImage = this.selectImage.bind(this);
  }

  public static async openDialog(dialogService: DialogService, data: NotificationTemplateDialogParams, config?: DynamicDialogConfig) {
    const isGlobal = !data.notificationTemplate.accountId;
    const header = isGlobal ? 'Global Notification Template Editor' : 'Account Notification Template Editor';

    const ref = dialogService.open(NotificationTemplateDialogComponent, {
      modal: true,
      closable: false,
      draggable: true,
      width: '800px',
      height: '80vh',
      header: header,
      focusOnShow: false,
      ...config,
      data: data,
    });
    return firstValueFrom(ref.onClose);
  }

  async ngOnInit() {
    const params = this.config.data;
    UtilFns.assertNonNull(params);

    this.isNew = !!params.isNew;
    this.isEditable = this.isNew || !!params.isEditable;

    this.notificationTemplate = params.notificationTemplate;
    this.accountId = params.accountId;
    this.dbSaveService = params.dbSaveService;

    this.isFreeForm = this.notificationTemplate.notificationEventId == NotificationEventEnum.FreeForm;

    if (this.isNew) {
      // Use pre-loaded templates if provided (e.g., for global templates), otherwise query
      const allTemplates = params.existingTemplates ?? await this.dbQueryService.getNotificationTemplates(params.accountId);
      this.templates = allTemplates.filter(NotificationTemplate.getTemplateTypeFilter(this.isFreeForm));
      if (this.notificationTemplate.notificationTypeId == null) {
        // Default to Email for EmailHeader/EmailFooter events, otherwise InApp
        this.notificationTemplate.notificationTypeId = this.getDefaultNotificationType();
      }
    }

    [this.notificationEvents, this.notificationTypes, this.notificationQueries] = await Promise.all([
      this.dbQueryService.getAll(NotificationEvent),
      this.dbQueryService.getAll(NotificationType),
      this.dbQueryService.getAll(NotificationQuery),
      this.loadAccountImages(),
    ]);

    this.notificationEvents = this.notificationEvents.filter((x) => x.id != NotificationEventEnum.FreeForm);

    if (this.notificationTemplate.notificationEventId == NotificationEventEnum.FreeForm) {
      this.namedNotificationQuery = this.notificationQueries.find((x) => x.name == 'AccountUser');
    }

    if (this.isNew && !this.isFreeForm) {
      this.onNotificationEventChange();
    } else {
      this.getContextMenuItems();
      this.getImageMenuItems();
    }

    this.isPageReady = true;

    if (this.isNew) {
      await UtilFns.wait(1);
      this.config.header = this.config.header + ' [ New ]';
    }
  }

  /** Get the default notification type based on the selected event */
  private getDefaultNotificationType(): NotificationTypeEnum {
    const eventId = this.notificationTemplate.notificationEventId;
    // EmailHeader and EmailFooter should default to Email type
    if (eventId === NotificationEventEnum.EmailHeader || eventId === NotificationEventEnum.EmailFooter) {
      return NotificationTypeEnum.Email;
    }
    return NotificationTypeEnum.InApp;
  }

  /** Check if the notification type should be locked based on the event */
  isNotificationTypeLocked(): boolean {
    const eventId = this.notificationTemplate.notificationEventId;
    // EmailHeader and EmailFooter must be Email type
    return eventId === NotificationEventEnum.EmailHeader || eventId === NotificationEventEnum.EmailFooter;
  }

  /** Get the available notification types based on the selected event */
  getAvailableNotificationTypes(): NotificationType[] {
    const eventId = this.notificationTemplate.notificationEventId;
    // EmailHeader and EmailFooter only allow Email type
    if (eventId === NotificationEventEnum.EmailHeader || eventId === NotificationEventEnum.EmailFooter) {
      return this.notificationTypes.filter(t => t.id === NotificationTypeEnum.Email);
    }
    return this.notificationTypes;
  }

  isDisabled() {
    return this.notificationTemplate.accountId == null;
  }

  canOk() {
    // Check basic required fields first
    const hasRequiredFields =
      this.isKeyUnique &&
      (this.notificationTemplate.notificationEventId != null || this.notificationTemplate.templateName != null) &&
      this.notificationTemplate.notificationTypeId != null;

    if (!hasRequiredFields) {
      return false;
    }

    // EmailHeader and EmailFooter are allowed to be empty — that's the supported way
    // to "disable" them so the Cerberus shell provides the only header/footer.
    if (this.isWrapperEvent()) {
      return true;
    }

    // Check if template content is not empty or whitespace-only
    // Strip HTML tags to check actual text content
    const template = this.notificationTemplate.template || '';
    const textContent = template.replace(/<[^>]*>/g, '').trim();

    return textContent.length > 0;
  }

  /** True for EmailHeader / EmailFooter — wrapper templates that may legitimately be empty. */
  private isWrapperEvent(): boolean {
    const eventId = this.notificationTemplate.notificationEventId;
    return eventId === NotificationEventEnum.EmailHeader || eventId === NotificationEventEnum.EmailFooter;
  }

  /** Validate and purify the template. Returns true if valid. */
  private async validateAndPurify(): Promise<boolean> {
    let ok = await this.checkIfUniqueKey();
    if (!ok) return false;

    // EmailHeader / EmailFooter are allowed to be empty (Cerberus shell takes over).
    const allowEmpty = this.isWrapperEvent();

    // Check BEFORE purifying to ensure we have content (unless this is a wrapper event)
    const prePurifyTemplate = this.notificationTemplate.template || '';
    const prePurifyText = prePurifyTemplate.replace(/<[^>]*>/g, '').trim();

    if (!prePurifyText && !allowEmpty) {
      await this.dialogService.warnDialog('Template content is required', 'The template cannot be empty or contain only whitespace.');
      return false;
    }

    if (prePurifyTemplate) {
      this.notificationTemplate.template = StringFns.purify(this.notificationTemplate.template, {
        ADD_ATTR: ['data-id', 'data-type'],
      });

      // Check again after purification in case purify stripped everything
      const postPurifyText = (this.notificationTemplate.template || '').replace(/<[^>]*>/g, '').trim();
      if (!postPurifyText && !allowEmpty) {
        await this.dialogService.warnDialog('Template content is required', 'The template content was invalid and could not be saved.');
        return false;
      }
    }

    ok = this.notificationTemplate.entityAspect.validateEntity();
    if (!ok) {
      this.handleBreezeValidationErrors([this.notificationTemplate]);
      return false;
    }
    return true;
  }

  async onOk() {
    const ok = await this.validateAndPurify();
    if (!ok) return;
    this.ref.close(true);
  }

  async onSave() {
    if (!this.dbSaveService) return;
    const ok = await this.validateAndPurify();
    if (!ok) return;

    await this.dbSaveService.saveSelectedChanges([this.notificationTemplate]);
    this.isSaved = true;
    this.isNew = false;
  }

  async onTemplateNameChange() {
    await this.checkIfUniqueKey();
  }

  async onNotificationTypeChange() {
    await this.checkIfUniqueKey();
  }

  async onNotificationEventChange() {
    if (this.notificationTemplate.notificationEvent == null) {
      return;
    }

    // Set the appropriate default notification type based on the event
    // EmailHeader and EmailFooter must be Email type
    if (this.isNotificationTypeLocked()) {
      this.notificationTemplate.notificationTypeId = NotificationTypeEnum.Email;
    }

    await this.checkIfUniqueKey();

    // Filter query types so they are unique for the event
    // No longer used - but maybe later...
    // const templatesWithSameEvent = this.templates?.filter(x => x.id != this.notificationTemplate.id &&
    //   x.notificationEventId == this.notificationTemplate.notificationEventId && x.accountId == this.notificationTemplate.accountId);
    // const existingTypes = templatesWithSameEvent?.map(x => x.notificationTypeId);

    this.getContextMenuItems();
    this.getImageMenuItems();
  }

  async checkIfUniqueKey() {
    if (this.isFreeForm) {
      this.isKeyUnique = await this.dbQueryService.checkIfIsUnique(this.notificationTemplate, 'id', 'templateName', 'accountId');
      if (!this.isKeyUnique) {
        await this.dialogService.warnDialog('Unable to use this name', 'This Template Name for this account has already been taken.');
        return false;
      }
    } else {
      this.isKeyUnique = await this.dbQueryService.checkIfIsUnique(
        this.notificationTemplate,
        'id',
        'notificationEventId',
        'notificationTypeId',
        'accountId',
      );
      if (!this.isKeyUnique) {
        // This should NEVER happen - if a template exists, we should be editing it, not creating a new one
        console.error('DUPLICATE TEMPLATE ERROR - This indicates a bug in template creation logic');
        console.error('Template being created:', {
          accountId: this.notificationTemplate.accountId,
          eventId: this.notificationTemplate.notificationEventId,
          typeId: this.notificationTemplate.notificationTypeId,
        });
        await this.dialogService.warnDialog(
          'BUG: Attempted to create duplicate template. This should have been caught earlier and the existing template should have been edited instead.',
          'System Error - Please report this',
        );
        return false;
      }
    }
    return true;
  }

  private getContextMenuItems() {
    // Get the template variables for the event's query - use either the notificationQueryId or AccountUser for free form context.
    const queryId = this.notificationTemplate.notificationEvent?.notificationQueryId || NotificationQueryEnum.AccountUser;
    this.templatesService.getNotificationMenuItems(queryId).then((x) => {
      this.contextMenuItems = x;
    });
  }

  /** Get the image menu items for the event's query - plus an additional item for selecting a SystemImage from the gallery */
  private getImageMenuItems() {
    const queryId = this.notificationTemplate.notificationEvent?.notificationQueryId;
    this.templatesService.getNotificationMenuItems(queryId).then((items) => {
      const imageItems = items.filter((x) => x.automationId.toLowerCase().includes('image')) as MenuItem[];
      const imgRoot = environment.apiUrl + 'img/';
      for (const item of imageItems) {
        // process menu entries to add image url
        const pt = item.automationId.toLowerCase().indexOf('image');
        // extract path, e.g. 'manufacturerImageId' => 'manufacturer'
        const path = pt == 0 ? 'image' : item.automationId.substring(0, pt);
        item.title = imgRoot + path + '/{{' + item.automationId + '}}';
      }
      // entry for custom images
      imageItems.push({ automationId: 'select', label: 'Acount Image', title: imgRoot + 'account/' });
      this.imageMenuItems = imageItems;
    });
  }

  /** Select/add a SystemImage from the file gallery, or just configure an image url from the MenuItem's title.
   * This function is called when selecting an item from the image menu of the HtmlEditor */
  async selectImage(item: MenuItem) {
    let url = item.title;
    let config: any = { url };
    if (item.automationId == 'select') {
      // select an image from the gallery
      const img = await this.imageHandler.openFileViewDialog(true);
      if (img) {
        await this.saveImages();
        url += img.id;
        config = { url, width: img.width, height: img.height };
      } else {
        // don't keep unsaved images in list
        this.oldImages = this.dbQueryService.uow.getEntities(AccountImage, true);
        return;
      }
    }
    config = this.baseService.dialogService.create<ImageInsertDialogParams>(
      ImageInsertDialogComponent,
      config,
      ImageInsertDialogComponent.config,
    );
    return config;
  }

  /** Save all changed Image and SystemImage entities */
  async saveImages() {
    await this.imageHandler.attachFiles();
    const imageChanges = this.dbQueryService.uow
      .getChanges()
      .filter((x) => x.entityType.shortName == 'AccountImage' || x.entityType.shortName == 'SystemImage');
    await this.dbQueryService.uow.saveChanges(imageChanges);
    this.oldImages = this.dbQueryService.uow.getEntities(AccountImage, true);
  }

  /** Load all AccountImages */
  private async loadAccountImages() {
    // Skip loading account images for global templates (no accountId)
    if (!this.accountId) {
      this.oldImages = [];
      return;
    }
    if (!this.oldImages || !this.oldImages.length) {
      this.oldImages = await this.dbQueryService.getAccountImagesByAccountId(this.accountId);
    }
  }

  handleBreezeValidationErrors(errEntities: Entity[]) {
    this.baseService.dialogService.validationDialog(this, errEntities, this.navigateToValidationError.bind(this), true);
  }

  navigateToValidationError(ee: EntityError, event: MouseEvent) {
    this.baseService.toastr.warning('Navigation to error not implemented', 'Not yet implemented');
  }

  onCancel() {
    if (this.isSaved) {
      // Already persisted via Save — close as success so caller refreshes
      this.ref.close(true);
    } else {
      this.notificationTemplate.entityAspect.rejectChanges();
      this.ref.close(false);
    }
  }

  canSave() {
    return this.dbSaveService && this.canOk() && this.hasChanges();
  }

  hasChanges() {
    return this.dbSaveService?.hasChanges();
  }

  canPreview() {
    // const user = this.baseService.authService.getUser();
    // return user?.accountId && this.messageTemplate.template && this.messageTemplate.template.includes('data-type="mention"');
    return this.notificationTemplate.template && this.notificationTemplate.template.includes('{{');
  }

  isEmail() {
    return this.notificationTemplate.notificationTypeId == NotificationTypeEnum.Email;
  }

  isSms() {
    return this.notificationTemplate.notificationTypeId == NotificationTypeEnum.SMS;
  }

  messageType() {
    return NotificationTypeEnum[this.notificationTemplate.notificationTypeId];
  }

  canSendTest() {
    // InApp notifications cannot be sent externally - they are created in the database
    // and displayed within the application when the user logs in
    return (
      this.notificationTemplate.notificationEventId != null &&
      this.notificationTemplate.notificationTypeId !== null &&
      this.notificationTemplate.notificationTypeId !== NotificationTypeEnum.InApp &&
      this.notificationTemplate.template
    );
  }

  private async promptForContext(query: NotificationQuery) {
    const ctx: { [key: string]: string | undefined } = {};
    const accountId = this.notificationTemplate.accountId || this.accountId!;

    const parameterNames = (query.parameterNames ?? '').split(',');
    for (const pn of parameterNames.map((pn) => pn.trim())) {
      if (pn == 'jobOrderId') {
        const jos = await this.baseService.dialogService.createFinder(CoreJobOrderFinderDialog, { accountId: accountId });
        if (!jos?.length) {
          return;
        }
        ctx[pn] = _.first(jos)?.id;
      }

      if (pn == 'accountId') {
        const accts = await this.baseService.dialogService.createFinder(CoreAccountFinderDialog, {});
        if (!accts?.length) {
          return;
        }
        ctx[pn] = _.first(accts)?.id;
      }

      if (pn == 'accountAdminId') {
        const allAccountAdmins = await this.dbQueryService.getAccountAdminsByAccountId(accountId);
        const aas = await this.baseService.dialogService.createFinder(AccountAdminFinderDialog, { accountAdmins: allAccountAdmins });
        if (!aas?.length) {
          return;
        }
        ctx[pn] = _.first(aas)?.id;
      }

      if (pn == 'accountUserId') {
        const allAccountUsers = await this.dbQueryService.getAccountUsers(accountId);
        const items = await this.baseService.dialogService.createFinder(AccountUserFinderDialog, { accountUsers: allAccountUsers });
        if (!items?.length) {
          return;
        }
        ctx[pn] = _.first(items)?.id;
      }

      if (pn == 'numberOfDays') {
        const numberOfDays = await this.baseService.dialogService.textpromptDialog('Enter number of days', { text: '0', pretext: '' });
        if (numberOfDays === undefined) {
          return;
        }
        ctx[pn] = numberOfDays;
      }

      if (pn == 'startDate') {
        const date = await this.baseService.dialogService.textpromptDialog('Enter start date', {
          text: this.startDate ?? '',
          pretext: 'format MM/DD/YYYY',
        });
        if (!date) {
          return;
        }
        ctx[pn] = date;
      }
    }

    if (Object.keys(ctx).length !== parameterNames.length) {
      await this.baseService.dialogService.okDialog('Unable to preview', 'Not all paramaters could be resolved');
      return null;
    }

    return JSON.stringify(ctx);
  }

  getNotificationQuery() {
    const ne = this.notificationTemplate.notificationEvent;
    if (ne != null) {
      return ne.notificationQuery;
    } else {
      return this.namedNotificationQuery!;
    }
  }

  async onPreview() {
    const query = this.getNotificationQuery();
    const json = await this.promptForContext(query);
    if (!json) {
      return;
    }

    const params: TemplatePreviewDialogParams = {
      template: this.notificationTemplate.template,
      isText: this.isSms(),
      queryId: query.id,
      context: json,
      wrapWithHeaderFooter: this.isEmail(),
    };
    // strange pathing...
    TemplatePreviewDialogComponent.openDialog(this.baseService.dialogService.pngDialogService, params);
  }

  async onSendNotification() {
    const query = this.getNotificationQuery();
    const json = await this.promptForContext(query);
    if (!json) {
      return;
    }

    if (this.notificationTemplate.notificationTypeId == NotificationTypeEnum.Email) {
      this.emailAddress = await this.baseService.dialogService.textpromptDialog('Enter email address', {
        text: this.emailAddress ?? '',
        posttext: 'Sending takes up to 60 seconds.  Check junk folder.',
      });
      if (!this.emailAddress) {
        return;
      }
    } else if (this.notificationTemplate.notificationTypeId == NotificationTypeEnum.SMS) {
      this.phone = await this.baseService.dialogService.textpromptDialog('Enter phone number', {
        text: this.phone ?? '',
        pretext: 'Phone number starting with +1',
      });
      if (!this.phone) {
        return;
      }
    }

    const user = {
      firstName: 'Prox',
      lastName: 'Tester',
      email: this.emailAddress ?? '',
      cellNumber: this.phone,
      userName: 'proxtester',
    };
    const body = {
      notificationEventId: this.notificationTemplate.notificationEventId,
      notificationTypeId: this.notificationTemplate.notificationTypeId,
      user,
      template: this.notificationTemplate.template,
      contextJson: json,
    };
    const url = environment.accountUrl + '/SendNotification';

    try {
      const response = await firstValueFrom(this.httpClient.post<{ Value: string }>(url, body));

      this.baseService.toastr.success(response.Value, 'Notification Status');
    } catch (ex) {
      this.baseService.toastr.error('Error sending: ' + UtilFns.getErrorMessage(ex), 'Notification Status');
    }
  }
}
